import React from 'react'
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  DollarSign,
  FileCheck2,
  PhoneMissed,
  UserPlus,
  Gauge,
} from 'lucide-react'
import { formatRelative } from '@/lib/format'
import type { InboxItem } from '@/hooks/useNotificationInbox'

// Shared row list for the bell (dropdown / phone sheet) and /notifications.

interface Tint { text: string; bg: string; border: string }
const NEUTRAL: Tint = { text: 'rgba(255,255,255,0.70)', bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)' }
const LAVENDER: Tint = { text: '#C7B9F0', bg: 'rgba(199,185,240,0.14)', border: 'rgba(199,185,240,0.28)' }
const MINT: Tint = { text: '#A8E6CE', bg: 'rgba(168,230,206,0.14)', border: 'rgba(168,230,206,0.28)' }
const BUTTER: Tint = { text: '#F3DF93', bg: 'rgba(243,223,147,0.14)', border: 'rgba(243,223,147,0.28)' }
const BLUSH: Tint = { text: '#F4B5BD', bg: 'rgba(244,181,189,0.14)', border: 'rgba(244,181,189,0.28)' }

const ICON = { width: 14, height: 14 }

function visualFor(item: InboxItem): { icon: React.ReactNode; tint: Tint } {
  switch (item.kind ?? item.eventType) {
    case 'new_lead':
    case 'LEAD_ASSIGNED':
      return { icon: <UserPlus style={ICON} />, tint: LAVENDER }
    case 'invoice_paid':
    case 'PAYMENT_RECEIVED':
      return { icon: <DollarSign style={ICON} />, tint: MINT }
    case 'job_completed':
      return { icon: <CheckCircle2 style={ICON} />, tint: BUTTER }
    case 'overdue_invoice':
    case 'JOB_OVERDUE':
      return { icon: <AlertTriangle style={ICON} />, tint: BLUSH }
    case 'QUOTE_APPROVED':
    case 'quote_signed':
      return { icon: <FileCheck2 style={ICON} />, tint: MINT }
    case 'missed_call':
      return { icon: <PhoneMissed style={ICON} />, tint: NEUTRAL }
    case 'phone_usage':
    case 'plan_usage':
      return { icon: <Gauge style={ICON} />, tint: NEUTRAL }
    default:
      return { icon: <Bell style={ICON} />, tint: NEUTRAL }
  }
}

export function NotificationList({
  items,
  onOpen,
}: {
  items: InboxItem[]
  onOpen: (item: InboxItem) => void
}) {
  return (
    <ul className="list-none m-0 p-0" data-testid="notification-list">
      {items.map((item) => {
        const { icon, tint } = visualFor(item)
        const unread = !item.readAt
        return (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onOpen(item)}
              data-unread={unread || undefined}
              className="w-full text-left px-3 py-2.5 hover:bg-white/[0.04] rounded-md transition-colors cursor-pointer flex gap-3 items-start"
              style={{ minHeight: 44 }}
            >
              <span
                aria-hidden
                className="w-8 h-8 rounded-[10px] shrink-0 flex items-center justify-center"
                style={{ background: tint.bg, border: `0.5px solid ${tint.border}`, color: tint.text }}
              >
                {icon}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block text-sm leading-snug ${unread ? 'text-white font-medium' : 'text-ink-300'}`}>
                  {item.title}
                </span>
                <span className="block text-[13px] leading-snug text-white/[0.55] line-clamp-2">{item.body}</span>
                <span className="block text-xs text-white/[0.62] mt-0.5">{formatRelative(item.createdAt)}</span>
              </span>
              {unread && (
                <span
                  className="mt-1.5 w-2 h-2 rounded-full shrink-0 bg-peach-200"
                  aria-label="Unread"
                  role="img"
                />
              )}
            </button>
          </li>
        )
      })}
    </ul>
  )
}
